// Localização: /frontend/src/components/Management/EquipamentoCard.js (versão 0.2.3)

import React from 'react';
// --- Ícones Lucide-React 
import { Pencil, Trash2, Link, Unlink, Wrench, Home, User, BatteryCharging, Calendar } from 'lucide-react'; 

// Mapeamento de Status para estilo (Padrão Emerald)
const statusStyles = {
    'Disponível': 'bg-green-100 text-green-800 border-green-400',
    'Em Uso': 'bg-blue-100 text-blue-800 border-blue-400',
    'Em Manutenção': 'bg-yellow-100 text-yellow-800 border-yellow-400',
    'Descartado': 'bg-red-100 text-red-800 border-red-400',
};

// Componente auxiliar para item de detalhe
const DetailItem = ({ Icon, label, value, colorClass = "text-emerald-600" }) => (
    <div className="flex items-center text-xs md:text-sm">
        <Icon className={`w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2 ${colorClass} flex-shrink-0`} />
        <span className="font-semibold mr-1 hidden md:inline">{label}:</span>
        <span className="font-semibold mr-1 md:hidden text-[10px]">{label.substring(0, 3)}:</span>
        <span className="truncate">{value}</span>
    </div>
);

/**
 * Cartão de exibição para um Equipamento, otimizado para layout de grid (mais quadrado).
 * @param {object} equipamento - Dados do equipamento.
 * @param {boolean} canManage - Indica se o usuário pode gerenciar o equipamento.
 * @param {function} onEdit - Handler para edição.
 * @param {function} onDelete - Handler para exclusão.
 * @param {function} onVincular - Handler para vincular a paciente.
 * @param {function} onDesvincular - Handler para desvincular de paciente.
 */
const EquipamentoCard = ({ equipamento, canManage, onEdit, onDelete, onVincular, onDesvincular }) => {
    
    const statusClass = statusStyles[equipamento.status] || 'bg-gray-100 text-gray-800 border-gray-400';
    const LocalIcon = equipamento.status === 'Em Uso' ? User : Home;
    const maintenanceDate = equipamento.data_proxima_manutencao 
        ? new Date(equipamento.data_proxima_manutencao).toLocaleDateString('pt-BR') 
        : 'N/A';

    return (
        // Layout: flex-col, h-full e justify-between para formato quadrado/coluna
        <div className="p-2 md:p-5 bg-white border-l-4 border-emerald-500 rounded-lg md:rounded-xl shadow-md md:shadow-lg hover:shadow-xl transition duration-300 flex flex-col h-full justify-between">
            
            {/* Seção Superior: Nome e Status */}
            <div>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-start mb-2 md:mb-3 gap-1 md:gap-0">
                    <h3 className="text-sm md:text-lg font-bold text-gray-900 leading-tight md:leading-snug break-words pr-1 md:pr-2 flex-1">
                        {equipamento.nome_equipamento}
                    </h3>
                    <span className={`px-1.5 md:px-2 py-0.5 text-[10px] md:text-xs font-medium rounded-full border ${statusClass} flex-shrink-0 whitespace-nowrap self-start md:self-auto`}>
                        {equipamento.status}
                    </span>
                </div>

                {/* Detalhes do Equipamento */}
                <div className="space-y-1 md:space-y-2 text-gray-700">
                    <DetailItem 
                        Icon={BatteryCharging} 
                        label="Série" 
                        value={equipamento.numero_serie} 
                    />
                    <DetailItem 
                        Icon={Calendar} 
                        label="Instalação" 
                        value={equipamento.data_instalacao ? new Date(equipamento.data_instalacao).toLocaleDateString('pt-BR') : 'N/A'}
                    />
                    
                    {/* Local / Paciente (Condicional) */}
                    {equipamento.status === 'Em Uso' ? (
                        <DetailItem 
                            Icon={User} 
                            label="Paciente" 
                            value={equipamento.paciente_vinculado_nome || 'Desconhecido'} 
                            colorClass="text-blue-500"
                        />
                    ) : (
                        <DetailItem 
                            Icon={Home} 
                            label="Local" 
                            value={equipamento.local_vinculado || 'Estoque'} 
                            colorClass="text-gray-500"
                        />
                    )}

                    {/* Próxima Manutenção (Condicional) */}
                    <DetailItem 
                        Icon={Wrench} 
                        label="Próx. Manut." 
                        value={maintenanceDate} 
                        colorClass={equipamento.data_proxima_manutencao ? "text-yellow-600" : "text-gray-400"}
                    />
                </div>
            </div>
            
            {/* Seção Inferior: Botões de Ação */}
            {canManage && (
                <div className="pt-2 md:pt-4 mt-2 md:mt-4 border-t border-gray-100 flex justify-center gap-1 md:space-x-3 flex-wrap">
                    
                    {/* Botão de Vínculo/Desvínculo */}
                    {equipamento.status === 'Disponível' && (
                        <button
                            className="text-white bg-blue-500 hover:bg-blue-600 p-1.5 md:p-2 rounded-lg shadow-md transition"
                            title="Vincular a Paciente"
                            onClick={() => onVincular(equipamento)}
                        >
                            <Link className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                    )}
                    {equipamento.status === 'Em Uso' && (
                        <button
                            className="text-white bg-red-500 hover:bg-red-600 p-1.5 md:p-2 rounded-lg shadow-md transition"
                            title="Desvincular de Paciente"
                            onClick={() => onDesvincular(equipamento)}
                        >
                            <Unlink className="w-4 h-4 md:w-5 md:h-5" />
                        </button>
                    )}

                    {/* Botão de Edição */}
                    <button 
                        className="text-gray-500 hover:text-emerald-600 transition p-1.5 md:p-2 bg-gray-100 rounded-lg" 
                        title="Editar Equipamento"
                        onClick={() => onEdit(equipamento)} 
                    >
                        <Pencil className="w-4 h-4 md:w-5 md:h-5" />
                    </button>

                    {/* Botão de Exclusão */}
                    <button 
                        className="text-gray-500 hover:text-red-600 transition p-1.5 md:p-2 bg-gray-100 rounded-lg" 
                        title="Excluir Equipamento"
                        onClick={() => onDelete(equipamento.id, equipamento.nome_equipamento)} 
                    >
                        <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default EquipamentoCard;